/**
 * ssoApi.ts
 * ---------
 * Tiny typed wrapper around the SSO-related Rails endpoints. Used by the
 * login page and the slug deep-link launcher. Note: this client is
 * intentionally separate from `authenticationApi` because it does NOT
 * carry an auth token (the user is, by definition, not logged in yet).
 */

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";

export type ValidateEmailChoice = {
  mode: "sp_initiated" | "redirect";
  slug: string;
  init_url?: string;
  redirect_url?: string;
  provider_label?: string | null;
  company_name?: string | null;
};

export type ValidateEmailResponse =
  | { sso: false }
  | (ValidateEmailChoice & { sso: true })
  | { sso: true; choices: ValidateEmailChoice[] };

/**
 * Resolve the absolute URL the browser should navigate to in order to
 * launch SSO for a given choice. SP-initiated flows hit a Rails URL that
 * crafts the SAMLRequest; redirect flows go straight to the customer's
 * portal URL.
 */
export function resolveSsoLaunchUrl(choice: ValidateEmailChoice): string {
  if (choice.mode === "redirect" && choice.redirect_url) {
    return choice.redirect_url;
  }
  if (choice.init_url) {
    return joinApiUrl(choice.init_url);
  }
  return joinApiUrl(`/v1/sso/init/${encodeURIComponent(choice.slug)}`);
}

/**
 * Call POST /api/v1/user_authentication/validate_email. Always uses POST
 * with a JSON body so the email never lands in URL/access logs.
 */
export async function validateEmail(
  email: string,
  options: { signal?: AbortSignal } = {},
): Promise<ValidateEmailResponse> {
  const url = joinApiUrl("/v1/user_authentication/validate_email");
  const res = await fetch(url, {
    method: "POST",
    signal: options.signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ p_email: email.trim().toLowerCase() }),
  });

  if (!res.ok) {
    let detail = "";
    try {
      detail = await res.text();
    } catch {
      detail = "";
    }
    throw new Error(
      `validate_email failed (${res.status})${detail ? `: ${detail}` : ""}`,
    );
  }

  return (await res.json()) as ValidateEmailResponse;
}

function joinApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = API_BASE_URL.replace(/\/+$/, "");
  const tail = path.startsWith("/") ? path : `/${path}`;
  // The Rails controller prefixes URLs with /api/v1/...; the front-end
  // base usually ends with /api, so we strip a duplicate /api segment
  // if both are present.
  if (base.endsWith("/api") && tail.startsWith("/api/")) {
    return base + tail.replace(/^\/api/, "");
  }
  return base + tail;
}

/** Persisted before IdP redirect so SSO failures can be reported with context. */
export const SSO_ATTEMPT_CONTEXT_KEY = "sso_attempt_context";

export type SsoAttemptContext = {
  email?: string;
  slug?: string;
};

export function readSsoAttemptContext(): SsoAttemptContext {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(SSO_ATTEMPT_CONTEXT_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as SsoAttemptContext;
  } catch {
    return {};
  }
}

export function writeSsoAttemptContext(context: SsoAttemptContext): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SSO_ATTEMPT_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    /* ignore quota / private mode */
  }
}

const SSO_ERROR_REPORTED_KEY = "sso_error_reported";

export type SsoErrorReportParams = {
  errorCode: string;
  errorMessage: string;
  userEmail?: string;
  slug?: string;
  source?: string;
};

async function fetchUserIpAddress(): Promise<string> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    const ipResponse = await fetch("https://api.ipify.org?format=json", {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (ipResponse.ok) {
      const ipData = (await ipResponse.json()) as { ip?: string };
      return ipData.ip || "Unknown";
    }
  } catch {
    /* non-fatal */
  }
  return "Unknown";
}

/**
 * Report an SSO failure to support via the unauthenticated error endpoint
 * (same path as authenticationApi uses when no session token is present).
 */
export async function reportSsoError(
  params: SsoErrorReportParams,
): Promise<void> {
  if (typeof window === "undefined") return;

  const userEmail = params.userEmail?.trim().toLowerCase() || "";
  const slug = params.slug?.trim() || "";
  const dedupeKey = `${params.errorCode}|${params.errorMessage}|${userEmail}|${slug}`;

  try {
    if (sessionStorage.getItem(SSO_ERROR_REPORTED_KEY) === dedupeKey) return;
    sessionStorage.setItem(SSO_ERROR_REPORTED_KEY, dedupeKey);
  } catch {
    /* continue even if sessionStorage is unavailable */
  }

  const currentPage = window.location.pathname;
  const referrer =
    typeof document !== "undefined" ? document.referrer || "N/A" : "N/A";
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : "N/A";
  const ipAddress = await fetchUserIpAddress();

  let errorDesc = `SSO sign-in failed.\n\nError code: ${params.errorCode}`;
  errorDesc += `\n\nUser message: ${params.errorMessage}`;
  if (userEmail) errorDesc += `\n\nEmail: ${userEmail}`;
  if (slug) errorDesc += `\n\nTenant slug: ${slug}`;
  if (params.source) errorDesc += `\n\nSource: ${params.source}`;
  errorDesc += `\n\nReferrer: ${referrer}`;
  errorDesc += `\n\nIP Address: ${ipAddress}`;
  errorDesc += `\n\nTimestamp: ${new Date().toISOString()}`;
  errorDesc = errorDesc.replace(/'/g, '"');

  const url = joinApiUrl("/v1/care_ascendium_error_outside/new");
  try {
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        p_request_path: "/v1/sso/validate",
        p_current_page: currentPage,
        p_ca_app: "Web",
        p_error_desc: errorDesc,
        p_ca_user_id: "",
        p_ca_user_email: userEmail,
        p_device_info: userAgent,
      }),
    });
  } catch (err) {
    console.error("reportSsoError failed", err);
  }
}
